import { createFileRoute, Outlet, Navigate } from "@tanstack/react-router"
import { lazy, Suspense } from "react"
import { authClient } from "@/lib/auth-client"
import { AuthenticatedShell } from "@/components/AuthenticatedShell"
import { PlaceDetailsProvider } from "@/lib/contexts/place-details-context"
import { defaultMeta, noIndex } from "@/lib/seo"

// Loaded lazily so the ~249 KB @heroui/react barrel isn't a static dep of the
// route tree (which would pull it onto the public marketing chunks). The authed
// shell pays for it behind Suspense — acceptable cost here.
const LazyHeroProvider = lazy(() => import(`@/components/LazyHeroProvider`))

export const Route = createFileRoute(`/_authenticated`)({
  ssr: false, // Disable SSR — auth gating runs client-side in the component
  head: () =>
    defaultMeta({
      title: `Account`,
      description: `Private ChicTravel account area.`,
      path: `/dashboard`,
      robots: noIndex,
    }),
  component: AuthenticatedLayout,
  // No `beforeLoad` session check: awaiting getSession there blocks the route
  // transition on a network round-trip before the page can render. Instead we
  // gate rendering in the component via `useSession()`, which reads from Better
  // Auth's shared client store (fetched once, cached) so navigation is instant
  // and unauthenticated users are redirected client-side.
  errorComponent: ({ error }) => {
    const ErrorComponent = () => {
      const { data: session } = authClient.useSession()

      // Only redirect to login if user is not authenticated
      if (!session && typeof window !== `undefined`) {
        window.location.href = `/login`
        return null
      }

      // For other errors, render an error message
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
            <p className="text-gray-600 mb-4">
              {error?.message || `An unexpected error occurred`}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Retry
            </button>
          </div>
        </div>
      )
    }

    return <ErrorComponent />
  },
})

function AuthenticatedLayout() {
  const { data: session, isPending } = authClient.useSession()

  // Wait for the session to resolve before deciding — rendering null here keeps
  // the screen blank for the brief first-load fetch rather than flashing the
  // page. On subsequent navigations the store is already populated so this is
  // synchronous.
  if (isPending) {
    return null
  }

  // Redirect unauthenticated users client-side. The child Outlet (and its tRPC
  // queries) never mount without a session, so there's no 401 storm.
  if (!session) {
    return <Navigate to="/login" />
  }

  // HeroUI mounts here (not at the root) so the marketing pages never pay for the
  // @heroui/react bundle — see __root.tsx. Lazy + Suspense keeps the barrel out
  // of the route tree's static graph.
  return (
    <Suspense fallback={null}>
      <LazyHeroProvider>
        <PlaceDetailsProvider>
          <AuthenticatedShell>
            <Outlet />
          </AuthenticatedShell>
        </PlaceDetailsProvider>
      </LazyHeroProvider>
    </Suspense>
  )
}
